import { Box, Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import CreateCampaignWizard from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/CreateCampaignWizard";
import { useManagedCampaigns } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/hooks/useManagedCampaigns";
import type {
  DisplayMarketingAdSet,
  DisplayMarketingCampaign,
} from "@common/interfaces/KYDMarketing";
import type {
  InstagramAccount,
  Step,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import actions from "src/actions";

type WizardStep = "none" | "edit_campaign" | "edit_adset" | "create_adset";

export default function CampaignDetailWizard({
  campaign,
  step,
  editingAdsetId,
  editingAdId,
  onClose,
  onSaved,
  onReady,
  initialWizardStep,
  onInitialStepConsumed,
}: {
  campaign: DisplayMarketingCampaign;
  step: WizardStep;
  editingAdsetId: string | null;
  /** When set with initialWizardStep "edit_creative", wizard shows this ad in the creative form. */
  editingAdId?: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onReady?: () => void;
  /** When set, wizard opens on this step (e.g. "review", "edit_creative") instead of the default edit step. */
  initialWizardStep?: Step | null;
  /** Called after applying initialWizardStep so parent can clear it. */
  onInitialStepConsumed?: () => void;
}) {
  const [pages, setPages] = useState<
    { id: string; name: string; picture: string }[]
  >([]);
  const [instagramAccounts, setInstagramAccounts] = useState<
    { id: string; username: string }[]
  >([]);
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [pixels, setPixels] = useState<{ id: string; name: string }[]>([]);
  const [audiences, setAudiences] = useState<any[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  const defaultEditingAdset: DisplayMarketingAdSet | null =
    editingAdsetId != null
      ? (campaign.adsets?.find((a) => a.id === editingAdsetId) ?? null)
      : null;

  const defaultEditingAd =
    editingAdId != null && defaultEditingAdset != null
      ? (defaultEditingAdset.ads?.find((a) => a.id === editingAdId) ??
        undefined)
      : undefined;

  const wizardStep: WizardStep = step === "none" ? "none" : step;

  const [currentStep, setCurrentStep] = useState<Step>(wizardStep);
  const [stepsHistory, setStepsHistory] = useState<Step[]>([]);

  useEffect(() => {
    if (wizardStep !== "none") {
      if (initialWizardStep) {
        setCurrentStep(initialWizardStep);
        setStepsHistory([wizardStep, initialWizardStep]);
        onInitialStepConsumed?.();
      } else {
        setCurrentStep(wizardStep);
        setStepsHistory([wizardStep]);
      }
    }
    // Only react to wizardStep so we don't revert to edit_adset when parent clears initialWizardStep
  }, [wizardStep]);

  const {
    loading: hookLoading,
    createCampaign,
    deleteAdset,
    deleteCreative,
    publishCampaign,
    unpublishCampaign,
    createAd,
    duplicateAdset,
    publishAdset,
    unpublishAdset,
    createAdCreative,
    fetchCampaigns,
    searchInterests,
    searchGeoMarkets,
    listPixels: hookListPixels,
    listInstagramAccounts: hookListInstagramAccounts,
    listPages: hookListPages,
  } = useManagedCampaigns({
    defaultCampaign: wizardStep !== "none" ? campaign : null,
    defaultEditingAdset: defaultEditingAdset ?? undefined,
    pages,
    instagramAccounts,
  });

  useEffect(() => {
    if (wizardStep === "none" || !campaign?.account_id) return;
    setRelatedLoading(true);
    Promise.all([
      hookListPages({ ad_account_id: campaign.account_id }).then(setPages),
      hookListInstagramAccounts({ ad_account_id: campaign.account_id }).then(
        setInstagramAccounts
      ),
      hookListPixels({ ad_account_id: campaign.account_id }).then(setPixels),
      campaign.account_id
        ? actions.ADMIN.listAudiences?.({ ad_account_id: campaign.account_id })
            .then((r: any) => setAudiences(r?.audiences ?? []))
            .catch(() => setAudiences([]))
        : Promise.resolve([]),
    ])
      .catch(console.error)
      .finally(() => {
        setRelatedLoading(false);
        onReady?.();
      });
  }, [
    wizardStep,
    campaign?.account_id,
    hookListPages,
    hookListInstagramAccounts,
    hookListPixels,
    onReady,
  ]);

  useEffect(() => {
    if (wizardStep === "none") return;
    actions.ADMIN.listAdAccounts?.()
      .then((r: any) => {
        const list = r?.accounts ?? r ?? [];
        setAccounts(Array.isArray(list) ? list : []);
      })
      .catch(() => setAccounts([]));
  }, [wizardStep]);

  const handleCreateCampaign = useCallback(
    async (payload: any) => {
      const res = await createCampaign(payload);
      await onSaved();
      return res;
    },
    [createCampaign, onSaved]
  );

  const handlePublishCampaign = useCallback(
    async (payload: any, extra?: any) => {
      await publishCampaign(payload, extra);
      await onSaved();
    },
    [publishCampaign, onSaved]
  );

  const handleUnpublishCampaign = useCallback(
    async (payload: any) => {
      await unpublishCampaign(payload);
      await onSaved();
    },
    [unpublishCampaign, onSaved]
  );

  const handlePublishAdset = useCallback(
    async (payload: any) => {
      await publishAdset(payload);
      await onSaved();
    },
    [publishAdset, onSaved]
  );

  const handleUnpublishAdset = useCallback(
    async (payload: any) => {
      await unpublishAdset(payload);
      await onSaved();
    },
    [unpublishAdset, onSaved]
  );

  const refetchAdsets = useCallback(
    async (_campaignId?: string) => {
      await onSaved();
    },
    [onSaved]
  );

  const setStep = useCallback(
    (s: Step) => {
      if (s === "none") {
        onClose();
        return;
      }
      setCurrentStep(s);
      setStepsHistory((prev) => (prev.includes(s) ? prev : [...prev, s]));
    },
    [onClose]
  );

  if (wizardStep === "none") return null;

  return (
    <Box
      w="full"
      h="full"
      minH={0}
      display="flex"
      flexDirection="column"
      bg="white"
      borderRadius="lg"
      overflow="hidden"
    >
      <HStack
        justify="space-between"
        px={6}
        py={4}
        borderBottomWidth="1px"
        borderColor="gray.200"
      >
        <Text fontWeight="600" fontSize="lg">
          {wizardStep === "edit_campaign"
            ? "Edit campaign"
            : wizardStep === "edit_adset"
              ? "Edit ad set"
              : "Edit"}
        </Text>
        <Button size="sm" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </HStack>
      <Box flex="1" overflowY="auto" px={6} py={4}>
        {relatedLoading ? (
          <VStack py={8}>
            <Spinner />
          </VStack>
        ) : (
          <CreateCampaignWizard
            step={currentStep}
            setStep={setStep}
            steps={stepsHistory}
            refetchAdsets={refetchAdsets}
            defaultCampaign={campaign}
            close={onClose}
            setDefaultCampaign={() => {}}
            defaultAd={defaultEditingAd}
            setDefaultEditingAd={() => {}}
            setDefaultEditingAdset={() => {}}
            onDuplicateAdset={duplicateAdset}
            onPublish={handlePublishCampaign}
            onCreateAd={createAd}
            onCreateAdCreative={createAdCreative}
            onCreateCampaign={handleCreateCampaign}
            onDeleteAdset={deleteAdset}
            onDeleteCreative={deleteCreative}
            loading={{
              ...hookLoading,
              publish: hookLoading.publish[campaign?.id ?? ""] ?? false,
            }}
            currency="USD"
            data={{
              name: campaign.name,
              description: "",
              link: "",
            }}
            audiences={audiences}
            campaigns={[campaign]}
            adsets={
              campaign.adsets?.map((a) => ({ id: a.id, name: a.name })) ?? []
            }
            defaultEditingAdset={defaultEditingAdset ?? undefined}
            adAccounts={accounts}
            pixels={pixels}
            pages={pages}
            instagramAccounts={instagramAccounts as InstagramAccount[]}
            search={{
              interests: searchInterests,
              geo_markets: searchGeoMarkets,
            }}
            createUploadUrl={actions.ADMIN.createUploadUrl}
            events={[]}
          />
        )}
      </Box>
    </Box>
  );
}
